import * as _ from "lodash";
import {IBusinessObject} from "../PA.Core/PA.Core.IF.BO";
import {IMessageProvider, IProgressProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {WB_ModelVolumeComponentRow} from "../PA.Core/PAdbSchemaRows";
import {IwbModelVolumeComponent} from "../PA.Core/PAdbSchemaRowsIF";
import {esWB_Model_Volume_Component} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {PAEnvironment} from "../PA.Core/PAEnvironment";


export class ModelVolumeComponentBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ModelVolumeComponent: WB_ModelVolumeComponentRow

    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New Model Volume Component" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);
        this.drWB_ModelVolumeComponent = null;
    }

    get WB_ModelVolumeComponent(): WB_ModelVolumeComponentRow {
        return this.drWB_ModelVolumeComponent;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        var self = this;
        this.LoadProgressProvider.start("Creating Model Volume Component");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Model Volume Component.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext
                .createRecordWithKey(esWB_Model_Volume_Component)
                .then((modelVolCompRow: WB_ModelVolumeComponentRow) => {
                    modelVolCompRow.setDefaults();
                    self.drWB_ModelVolumeComponent = modelVolCompRow;
                    //add this to all the BOs
                    self.dbContext.add(esWB_Model_Volume_Component, modelVolCompRow);

                    self.Identity.setAll(
                        self.WB_ModelVolumeComponent.cif.WB_MODEL_VOLUME_COMPONENT_ID,
                        self.WB_ModelVolumeComponent.cif.VOLUME_COMPONENT_ID,
                        self.WB_ModelVolumeComponent.cif.VOLUME_COMPONENT_NAME
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_ModelVolumeComponent,
                        "WB_MODEL_VOLUME_COMPONENT_ID",
                        "VOLUME_COMPONENT_ID",
                        "VOLUME_COMPONENT_NAME"
                    );

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch(err => {
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        var self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        if (!this.bIsLoaded)
            this.Identity.setAll(anId, anId, "Loading Model Volume Component (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

            if (self.bIsLoaded) {
                var sError = "Model Volume Component already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Model Volume Component.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }
            //Added for record resolver
            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = ModelVolumeComponentBO.createRecordResolver({
                    dbContext: self.dbContext
                });
                recordResolverLoadPromise = ModelVolumeComponentBO.setupRecordResolver(
                    recordResolver,
                    [anId]
                );
            }

            recordResolverLoadPromise
                .then(function() {
                    let newModelVolumeCompRow: any = recordResolver.get(
                        esWB_Model_Volume_Component,
                        anId
                    );

                    if (_.isEmpty(newModelVolumeCompRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_ModelVolumeComponent = newModelVolumeCompRow;
                    self.Identity.setAll(
                        self.WB_ModelVolumeComponent.cif.WB_MODEL_VOLUME_COMPONENT_ID,
                        self.WB_ModelVolumeComponent.cif.VOLUME_COMPONENT_ID,
                        self.WB_ModelVolumeComponent.cif.VOLUME_COMPONENT_NAME
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_ModelVolumeComponent,
                        "WB_MODEL_VOLUME_COMPONENT_ID",
                        "VOLUME_COMPONENT_ID",
                        "VOLUME_COMPONENT_NAME"
                    );


                })
                .then((newModelVolumeCompRow: any) => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                    return;
                });
        });

        return this._OnLoadComplete;
    }


    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {
        const self = this;

        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start(
            "Validating " + self.WB_ModelVolumeComponent.cif.WB_MODEL_VOLUME_COMPONENT_ID +
            "(" + self.WB_ModelVolumeComponent.cif.VOLUME_COMPONENT_ID + ")"
        );

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (_.isEmpty(self.dbContext)) {
                let sError = "Unable to determine dbContext for to Validate Volume Component.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }
            self.ValidateMessageProvider.clearMessages()
            let msgErrors: {title: string, code: string}[] = []
            if(_.isEmpty(self.drWB_ModelVolumeComponent.cif.VOLUME_COMPONENT_ID)){
                msgErrors.push({title: "Id not Set", code: "IdNotSet"})
            }

            if(_.isEmpty(self.drWB_ModelVolumeComponent.cif.VOLUME_COMPONENT_NAME)){
                msgErrors.push({title: "Name not Set", code: "NameNotSet"})
            }

            if(_.isEmpty(self.drWB_ModelVolumeComponent.cif.VOLUME_COMPONENT_TYPE)){
                msgErrors.push({title: "Component Type not Set", code: "VCNotSet"})
            }

            _.forEach(msgErrors, (a) => {
                self.ValidateMessageProvider.addMessage({type: MessageType.Error, title: a.title, code: a.code, description: a.title})
            })

            self._bIsValidated = (self.ValidateMessageProvider.countMessages(MessageType.Error) == 0)

            self.ValidateProgressProvider.finish();

            resolve(self);
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbModelVolumeComponent = <IwbModelVolumeComponent>self.WB_ModelVolumeComponent;

        self.SaveProgressProvider.start(
            "Saving " +
            wbModelVolumeComponent.VOLUME_COMPONENT_NAME +
            "(" + wbModelVolumeComponent.VOLUME_COMPONENT_ID + ")"
        );

        self._bIsSaved = false

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (_.isEmpty(self.dbContext)) {
                let sError = "Unable to determine dbContext to Save Volume Component";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return;
            }


            self.SaveOptions = options || {};
            let bDone = false

            let saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any
            ) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }


            self.Validate({})
                .then((ignore) => {
                    if(self.bIsValidated)
                        return saveFunction(esWB_Model_Volume_Component, self.drWB_ModelVolumeComponent, {})

                    bDone = true
                })
                .then(ignored => {
                    if(bDone)
                        return
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        });
                    } else {
                        self._bIsSaved = true;
                    }
                })
                .then(ignored => {
                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch(err => {
                    self.SaveProgressProvider.exception(err);
                    resolve(self);
                });
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(
        bSaveNow: boolean,
        options?: any
    ): Promise<IBusinessObject> {
        var self = this;
        var wbModelVolumeComponent = <IwbModelVolumeComponent>self.drWB_ModelVolumeComponent;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            "Deleting " + wbModelVolumeComponent.VOLUME_COMPONENT_NAME + "(" + wbModelVolumeComponent.VOLUME_COMPONENT_ID + ")"
        );
        return new Promise<IBusinessObject>((resolve, error) => {
            // 1. Validate if we can delete the Meta

            // 2. If Valid, mark all the records for deletion.
            self.drWB_ModelVolumeComponent.markDeleted();

            if (bSaveNow) {
                self
                    .SaveUsing(false)
                    .then(ignore => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch(err => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>


    //<editor-fold desc="IHaveRecordResolver -- static">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const modelvolumecomp_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, modelvolumecomp_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Model_Volume_Component, ids)
                .then(result => {
                    resolve();
                })
                .catch(err => {
                    error(err);
                });
        });
    }

    //</editor-fold>

}
PAEnvironment.registerBusinessObjectClass( ModelVolumeComponentBO, "ModelVolumeComponentBO" ,"Model Volume Component")